import { deleteObject, getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { storage } from "@/lib/firebase";
import { compressImage } from "@/lib/utils";

export interface StoredQualiReparAsset {
  url: string;
  path: string;
  fileName: string;
  contentType: string;
}

function sanitizeSegment(value: string) {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [metadata, content] = dataUrl.split(",");
  if (!metadata || !content) {
    throw new Error("Signature invalide.");
  }

  const mimeMatch = metadata.match(/data:(.*?);base64/);
  const contentType = mimeMatch?.[1] || "image/png";
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: contentType });
}

async function uploadAsset(path: string, file: Blob, contentType: string): Promise<StoredQualiReparAsset> {
  const assetRef = ref(storage, path);
  await uploadBytes(assetRef, file, { contentType });

  return {
    url: await getDownloadURL(assetRef),
    path,
    fileName: path.split("/").pop() || path,
    contentType,
  };
}

export async function uploadQualiReparSignature(signatureDataUrl: string, claimId: string): Promise<StoredQualiReparAsset> {
  const blob = dataUrlToBlob(signatureDataUrl);
  const extension = blob.type === "image/jpeg" ? "jpg" : "png";
  const path = `qualirepar_claims/${sanitizeSegment(claimId)}/signature.${extension}`;
  return uploadAsset(path, blob, blob.type || "image/png");
}

export async function uploadQualiReparNameplate(file: File, claimId: string, serialNumber: string): Promise<StoredQualiReparAsset> {
  const basePath = `qualirepar_claims/${sanitizeSegment(claimId)}/nameplate_${sanitizeSegment(serialNumber)}`;

  // Une photo brute de tablette dépasse vite 4 Mo, au-delà de ce que l'API peut
  // renvoyer pour reprendre le dossier : on archive une version allégée mais lisible.
  try {
    const compressed = dataUrlToBlob(await compressImage(file, { maxWidth: 2000, maxHeight: 2000, quality: 0.85 }));
    if (compressed.type === "image/jpeg") {
      return uploadAsset(`${basePath}.jpg`, compressed, "image/jpeg");
    }
  } catch (error) {
    console.warn("[QualiRépar] Compression de la plaque impossible, archivage de l'original:", error);
  }

  const extension = (file.name.split(".").pop() || "jpg").toLowerCase();
  return uploadAsset(`${basePath}.${extension}`, file, file.type || "image/jpeg");
}

export async function deleteQualiReparAsset(path?: string | null): Promise<void> {
  if (!path) return;

  try {
    await deleteObject(ref(storage, path));
  } catch (error: any) {
    if (error?.code !== "storage/object-not-found") {
      throw error;
    }
  }
}

// Au-delà, on abandonne : le formulaire « Refaire » reste bloqué tant que la
// récupération n'a pas abouti, et le SDK Storage réessaie jusqu'à 2 min.
const ASSET_LOAD_TIMEOUT_MS = 15_000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Délai de récupération du fichier archivé dépassé.")), timeoutMs);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

// Le bucket Storage ne renvoie pas d'en-tête CORS : `getBlob` et `fetch(url)`
// sont bloqués par le navigateur. On passe donc par l'API, qui télécharge le
// fichier côté serveur ; `getBlob` reste un repli si le proxy est indisponible.
export async function loadStoredQualiReparAsset(asset: StoredQualiReparAsset): Promise<Blob> {
  let proxyError: unknown = null;

  if (asset.url) {
    try {
      const response = await fetch(`/api/qualirepar-asset?url=${encodeURIComponent(asset.url)}`, {
        signal: AbortSignal.timeout(ASSET_LOAD_TIMEOUT_MS),
      });
      if (!response.ok) {
        throw new Error(`Impossible de récupérer le fichier archivé (${response.status}).`);
      }
      return await response.blob();
    } catch (error) {
      proxyError = error;
    }
  }

  if (asset.path) {
    try {
      return await withTimeout(getBlob(ref(storage, asset.path)), ASSET_LOAD_TIMEOUT_MS);
    } catch (error) {
      console.error("[QualiRépar] Récupération du fichier archivé impossible:", { path: asset.path, proxyError, error });
      throw proxyError || error;
    }
  }

  throw proxyError || new Error("Fichier archivé sans emplacement.");
}

export async function loadStoredQualiReparFile(asset: StoredQualiReparAsset): Promise<File> {
  const blob = await loadStoredQualiReparAsset(asset);
  return new File([blob], asset.fileName || "plaque-signaletique.jpg", {
    type: asset.contentType || blob.type || "image/jpeg",
  });
}

export async function loadStoredQualiReparDataUrl(asset: StoredQualiReparAsset): Promise<string> {
  const blob = await loadStoredQualiReparAsset(asset);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Impossible de lire le fichier archivé."));
    reader.readAsDataURL(blob);
  });
}

export async function uploadQualiReparPdf(pdfDataUrl: string, claimId: string, type: "attestation" | "invoice"): Promise<StoredQualiReparAsset> {
  const blob = dataUrlToBlob(pdfDataUrl);
  const path = `qualirepar_claims/${sanitizeSegment(claimId)}/${type}.pdf`;
  return uploadAsset(path, blob, "application/pdf");
}

export async function uploadSalePdf(pdfDataUrl: string, saleId: string, type: "invoice" | "credit_note"): Promise<StoredQualiReparAsset> {
  const blob = dataUrlToBlob(pdfDataUrl);
  const path = `sales/${sanitizeSegment(saleId)}/${type}.pdf`;
  return uploadAsset(path, blob, "application/pdf");
}
